import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { DEFAULT_PAGING, Filter, OrganizationService } from 'src/app/@shared';
import { environment } from 'src/environments/environment';
import { DEFAULT_DATE_RANGE } from './event.service';
import { v4 as uuidv4 } from 'uuid';
import { CustomFieldDefinition } from '../models/custom-field.model';

@Injectable({
  providedIn: 'root',
})
export class CustomFieldService {
  private dateRangeBehaviorSubject = new BehaviorSubject(DEFAULT_DATE_RANGE);
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private pageBehaviorSubject = new BehaviorSubject({
    ...DEFAULT_PAGING,
    pageSize: 20,
  });
  private sortBehaviorSubject = new BehaviorSubject({
    active: 'StartDate',
    direction: 'desc',
  });
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private viewModeBehaviorSubject = new BehaviorSubject<string>('TABLE');
  private filterBehaviorSubject = new BehaviorSubject<Filter[]>([]);

  // we do not wish to expose our behavior subjects.  create public observables
  public dateRange$ = this.dateRangeBehaviorSubject.asObservable();
  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public page$ = this.pageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public viewMode$ = this.viewModeBehaviorSubject.asObservable();
  public filters$ = this.filterBehaviorSubject.asObservable();
  public reload$ = this.reloadBehaviorSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private organizationService: OrganizationService
  ) {}

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public params$ = combineLatest([
    this.pageBehaviorSubject.pipe(debounceTime(300)), // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([page, sort, search, reload]) => {
      let _orderby = `${sort.direction}`;
      if (sort.active == 'DivisionNames' || sort.active == 'EventTypeName') {
        _orderby = `${sort.active} ${sort.direction}`;
      }

      // set the query string parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $count: true,
        },
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  // sets the sort property and order
  sort(sort: any) {
    this.sortBehaviorSubject.next(sort);
  }

  reload() {
    this.reloadBehaviorSubject.next(uuidv4());
  }

  page(page: any) {
    this.pageBehaviorSubject.next(page);
  }

  // sets the search phrase
  search(search: string) {
    this.searchBehaviorSubject.next(search);
    let paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.pageBehaviorSubject.next(paging);
  }

  private customFieldResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/CustomFieldDefinitions/GetCustomFieldDefinitions`,
        { params: _params }
      )
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // event listing
  public customFields$: Observable<CustomFieldDefinition[]> = this.customFieldResponse$.pipe(
    map((res: any) => res)
  );

  public totalRecords$: Observable<number> = this.customFieldResponse$.pipe(
    map((res: any) => res?.length || 0)
  );

  createCustomField(fieldDefinition: any) {
    return this.httpClient.post(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/CustomFieldDefinitions/CreateCustomFieldDefinition`,
      fieldDefinition
    );
  }

  getCustomFieldDefinition(id: string): Observable<CustomFieldDefinition> {
    return this.httpClient.get<CustomFieldDefinition>(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/CustomFieldDefinitions/GetCustomFieldDefinition?key=${id}`
    );
  }

  getCustomFieldDefinitionsByVertexType(vertexType: string): Observable<CustomFieldDefinition[]> {
    return this.httpClient.get<CustomFieldDefinition[]>(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/CustomFieldDefinitions/GetCustomFieldDefinitionsByVertexType?vertexType=${vertexType}`
    );
  }

  updateCustomField(id: string, fieldDefinition: any) {
    return this.httpClient.put(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/CustomFieldDefinitions/UpdateCustomFieldDefinition?key=${id}`,
      fieldDefinition
    );
  }

  deleteCustomFields(ids: string[]) {
    let params = new HttpParams();
    ids.forEach((id) => {
      params = params.append('customFieldDefinitionIds', id);
    });
    return this.httpClient.post(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/CustomFieldDefinitions/DeleteCustomFieldDefinitions`, {},
      { params }
    );
  }
}
